"use client";

import { Button } from "@/components/atoms/Button";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  leadCtaClick,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as z from "@/lib/propSchema";
import { LEAD_DROP_VARIANTS } from "../../../constants";

const CategorySchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v) => (v == null ? "" : String(v))),
  content: z.string().optional().default(""),
  order_index: z.coerce.number().optional().default(0),
  is_active: z.union([z.boolean(), z.number(), z.string()]).optional(),
  status: z.union([z.boolean(), z.number(), z.string()]).optional(),
});

const Eligibility2DataSchema = z
  .object({
    title: z.string().optional().default("Eligibility Criteria"),
    title_text_color: z.string().optional().default("#F8F9FA"),
    background_gradient_start_color: z.string().optional().default("#0A3D5B"),
    background_gradient_end_color: z.string().optional().default("#0A3D5B"),
    background_gradient_angle: z.string().optional().default("180deg"),
    category_border_color: z.string().optional().default("#FFFFFF"),
    category_active_text_color: z.string().optional().default("#0A3D5B"),
    category_active_background_color: z.string().optional().default("#FFFFFF"),
    category_inactive_text_color: z.string().optional().default("#F8F9FA"),
    content_text_color: z.string().optional().default("#F8F9FA"),
    button_text: z.string().optional().default(""),
    button_text_color: z.string().optional().default("#222222"),
    button_color: z.string().optional().default("#FFFFFF"),
    read_more_url: z.string().optional().default(""),
    status: z.union([z.boolean(), z.number(), z.string()]).optional(),
    categories: z
      .union([z.array(CategorySchema), z.null()])
      .optional()
      .transform((c) => c ?? []),
  })
  .transform((data) => ({
    ...data,
    categories: [...(data.categories || [])].sort(
      (a, b) => (a.order_index || 0) - (b.order_index || 0),
    ),
  }));

type Eligibility2Data = z.infer<typeof Eligibility2DataSchema>;

/** Treat DB/JSON booleans: false, 0, "0" off; true, 1, "1", undefined on */
function isActiveFlag(v: unknown): boolean {
  if (v === false || v === 0 || v === "0") return false;
  return true;
}

function isSectionEnabled(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (v === false || v === 0 || v === "0") return false;
  if (v === true || v === 1 || v === "1") return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "false" || s === "off" || s === "no") return false;
  }
  return true;
}

function isRenderableCategory(c: {
  name?: string;
  status?: unknown;
  is_active?: unknown;
}): boolean {
  return (
    isActiveFlag(c.status) &&
    isActiveFlag(c.is_active) &&
    String(c.name ?? "").trim().length > 0
  );
}

/** Only http(s) URLs or same-origin relative paths; blocks javascript: etc. */
function getSafeCtaHref(raw: string | undefined): string | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  if (typeof window === "undefined") return null;
  try {
    const base = window.location.origin;
    const u = new URL(s, base);
    if (u.protocol === "http:" || u.protocol === "https:") {
      return u.href;
    }
    return null;
  } catch {
    return null;
  }
}

export function Eligibility2(props: any) {
  const actualData = props?.data?.data ?? props?.data ?? props;
  const dispatch = useDispatch();
  const sectionRef = useRef<HTMLElement>(null);
  const [activeId, setActiveId] = useState<string | number | null>(null);

  // Parents (e.g. admin preview) often pass a new object reference every render with the same
  // payload. Memoizing on `actualData` alone remakes `parseResult` each time, so the active-tab
  // effect re-runs, calls setActiveId, and triggers "Maximum update depth exceeded".
  let actualDataSerialized: string;
  try {
    actualDataSerialized = JSON.stringify(actualData ?? null);
  } catch {
    actualDataSerialized = "\0__eligibility2_json__";
  }

  const parseResult = useMemo(() => {
    const raw =
      actualDataSerialized === "\0__eligibility2_json__"
        ? actualData
        : (JSON.parse(actualDataSerialized) as unknown);
    return Eligibility2DataSchema.safeParse(raw);
  }, [actualDataSerialized]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (sectionRef.current) {
  //     FireWidgetViewedEvent(sectionRef, "eligibility_criteria_section_v2");
  //   }
  // }, []);

  useEffect(() => {
    if (!parseResult.success) return;
    const list = parseResult.data.categories.filter(isRenderableCategory);
    if (list.length === 0) {
      setActiveId(null);
      return;
    }
    setActiveId((prev) => {
      if (prev != null && list.some((c) => String(c.id) === String(prev))) {
        return prev;
      }
      return list[0].id;
    });
  }, [parseResult]);

  if (!parseResult.success) {
    console.error("Eligibility2 validation failed:", parseResult.error);
    return null;
  }

  const parsed: Eligibility2Data = parseResult.data;
  if (!isSectionEnabled(parsed.status)) return null;

  const categories = parsed.categories.filter(isRenderableCategory);
  const hasHeading = String(parsed.title ?? "").trim().length > 0;
  if (!hasHeading && categories.length === 0) return null;

  const active =
    categories.find((c) => String(c.id) === String(activeId)) ?? categories[0];

  const bgStyle: React.CSSProperties =
    parsed.background_gradient_start_color &&
    parsed.background_gradient_end_color &&
    parsed.background_gradient_start_color !==
      parsed.background_gradient_end_color
      ? {
          background: `linear-gradient(${parsed.background_gradient_angle}, ${parsed.background_gradient_start_color}, ${parsed.background_gradient_end_color})`,
        }
      : {
          backgroundColor: parsed.background_gradient_start_color || "#0A3D5B",
        };

  const handleCta = () => {
    leadCtaClick({
      section: "eligibility_criteria_section_v2",
      component: "cta_button",
      label: parsed.button_text || "",
    });
    const href = getSafeCtaHref(parsed.read_more_url);
    if (href) {
      window.location.assign(href);
      return;
    }
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  };

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative w-full flex flex-col items-center",
        "px-5 py-11",
        "sm:px-[60px]",
        "md:px-[80px]",
        "xl:px-[100px]",
      )}
      style={bgStyle}
    >
      <div
        className={cn(
          "flex flex-col items-center w-full max-w-[1100px]",
          "gap-6",
        )}
      >
        <h2
          className={cn(
            "w-full text-center font-bold text-[28px] leading-normal",
            "xl:font-semibold xl:text-[34px]",
            "type-h3",
          )}
          style={{ color: parsed.title_text_color }}
        >
          {parsed.title}
        </h2>

        {categories.length > 0 ? (
          <div
            className={cn(
              "flex w-full flex-wrap justify-start gap-3.5",
              "content-start",
            )}
          >
            {categories.map((cat) => {
              const selected = String(cat.id) === String(active?.id);
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveId(cat.id)}
                  className={cn(
                    "rounded-[10px] px-4 py-2.5 text-base font-semibold border border-solid transition-colors",
                    "xl:px-5 xl:py-3.5 xl:text-lg",
                    selected ? "font-semibold" : "font-normal",
                    "type-body",
                  )}
                  style={{
                    borderColor: parsed.category_border_color,
                    backgroundColor: selected
                      ? parsed.category_active_background_color
                      : "transparent",
                    color: selected
                      ? parsed.category_active_text_color
                      : parsed.category_inactive_text_color,
                  }}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
        ) : null}

        {categories.length > 0 && (active?.content || "").trim().length > 0 ? (
          <div
            className={cn(
              "w-full text-sm leading-normal [&_a]:underline [&_li]:mb-1.5 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6",
              "sm:text-base",
              "xl:text-lg",
              "type-body-sm",
            )}
            style={{ color: parsed.content_text_color }}
            dangerouslySetInnerHTML={{ __html: active?.content || "" }}
          />
        ) : null}

        {parsed.button_text ? (
          <div className="flex justify-center items-center w-full">
            <Button
              type="button"
              onClick={handleCta}
              className={cn(
                "font-semibold rounded-lg px-6 py-3 text-base",
                "sm:px-5 sm:py-3.5",
                "xl:text-lg xl:px-6 xl:py-4",
                "type-h6",
              )}
              style={{
                backgroundColor: parsed.button_color,
                color: parsed.button_text_color,
              }}
            >
              {parsed.button_text}
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
